import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Star, Trash2, Plus, Sparkles, Loader2 } from "lucide-react";

interface Fav {
  id: string; keyword: string; intent: string; volume_estimate: string; difficulty: string; notes: string;
}

const KeywordFavorites = ({ onUse, onGenerated }: { onUse: (kw: string) => void; onGenerated?: (articleId: string) => void }) => {
  const [items, setItems] = useState<Fav[]>([]);
  const [q, setQ] = useState("");
  const [manual, setManual] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    const { data } = await (supabase as any).from("blog_keyword_favorites").select("*").order("created_at", { ascending: false });
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const addManual = async () => {
    const k = manual.trim();
    if (!k) return;
    const { error } = await (supabase as any).from("blog_keyword_favorites").insert({ keyword: k });
    if (error) { toast.error(error.message); return; }
    setManual("");
    toast.success("Mot-clé ajouté");
    load();
  };

  const remove = async (id: string) => {
    const { error } = await (supabase as any).from("blog_keyword_favorites").delete().eq("id", id);
    if (error) toast.error(error.message);
    else { toast.success("Supprimé"); load(); }
  };

  const toggle = (kw: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(kw)) next.delete(kw); else next.add(kw);
      return next;
    });
  };

  const generateFromSelection = async () => {
    const kws = Array.from(selected);
    if (kws.length === 0) { toast.error("Sélectionnez au moins un mot-clé favori"); return; }
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("seo-generate-article", {
        body: { keywords: kws, internalUrl: "https://maroctransfers.com/" },
      });
      if (error) throw error;
      const article = (data as any)?.article;
      if (!article?.id) throw new Error("Pas d'article généré");
      toast.success("Brouillon créé — direction l'audit");
      setSelected(new Set());
      onGenerated?.(article.id);
    } catch (e: any) {
      toast.error(e?.message || "Échec de la génération");
    } finally {
      setGenerating(false);
    }
  };

  const filtered = items.filter((i) => i.keyword.toLowerCase().includes(q.toLowerCase()));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Star className="h-5 w-5 text-amber-500" /> Bibliothèque de mots-clés favoris
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/30 p-3">
          <p className="text-sm">
            <span className="font-medium">{selected.size}</span> mot{selected.size > 1 ? "s" : ""}-clé{selected.size > 1 ? "s" : ""} sélectionné{selected.size > 1 ? "s" : ""}
          </p>
          <Button
            onClick={generateFromSelection}
            disabled={generating || selected.size === 0}
            className="bg-gradient-gold text-primary-foreground"
          >
            {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            Générer un article
          </Button>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input placeholder="Rechercher dans les favoris…" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="flex gap-2">
            <Input placeholder="Ajouter manuellement…" value={manual} onChange={(e) => setManual(e.target.value)} />
            <Button onClick={addManual}><Plus className="h-4 w-4 mr-1" />Ajouter</Button>
          </div>
        </div>
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun mot-clé favori. Utilisez l'étoile dans la recherche pour en ajouter.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((f) => (
              <div key={f.id} className="rounded-lg border border-border p-3 bg-card flex flex-col gap-2">
                <div className="flex items-start gap-2">
                  <Checkbox
                    checked={selected.has(f.keyword)}
                    onCheckedChange={() => toggle(f.keyword)}
                    className="mt-0.5"
                    aria-label={`Sélectionner ${f.keyword}`}
                  />
                  <div className="font-medium text-sm flex-1">{f.keyword}</div>
                </div>
                {f.notes && <p className="text-xs text-muted-foreground line-clamp-2">{f.notes}</p>}
                <div className="flex gap-2 mt-auto">
                  <Button size="sm" variant="default" className="bg-gradient-gold text-primary-foreground" onClick={() => onUse(f.keyword)}>
                    Utiliser
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(f.id)} className="text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default KeywordFavorites;